import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/upr-api";

const DEFAULTS = {
  logo_url: "",
  logo_text: "UNIVERSO POSITIVO",
  logo_tagline: "Records · Est. since the sun",
  use_default_logo: true,
  primary_color: "#D95A3B",
  font_family: "sans",
  heading_scale: 1.0,
  body_size: "base",
  site_title: "Universo Positivo Records",
  meta_description: "Independent record label for soulful, sun-soaked, hand-played music. Lisbon × Amsterdam.",
  footer_tagline: "Music from the sun. Hand-pressed in Lisbon.",
  footer_about: "Universo Positivo Records is an independent label releasing soulful, sun-soaked, hand-played music.",
};

const SettingsContext = createContext(null);

function darken(hex, amount = 0.12) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  let r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  r = Math.max(0, Math.round(r * (1 - amount)));
  g = Math.max(0, Math.round(g * (1 - amount)));
  b = Math.max(0, Math.round(b * (1 - amount)));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("").toUpperCase();
}

function applyToDocument(s) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--upr-primary", s.primary_color);
  root.style.setProperty("--upr-primary-hover", darken(s.primary_color, 0.14));
  root.style.setProperty("--upr-heading-scale", String(s.heading_scale ?? 1.0));
  const bodySizes = { sm: "14px", base: "16px", lg: "18px" };
  root.style.setProperty("--upr-body-size", bodySizes[s.body_size] || "16px");
  root.setAttribute("data-font", s.font_family || "sans");

  if (s.site_title) document.title = s.site_title;
  let desc = document.querySelector('meta[name="description"]');
  if (!desc) {
    desc = document.createElement("meta");
    desc.setAttribute("name", "description");
    document.head.appendChild(desc);
  }
  desc.setAttribute("content", s.meta_description || "");
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const merged = { ...DEFAULTS, ...data };
      setSettings(merged);
      applyToDocument(merged);
    } catch (e) {
      applyToDocument(DEFAULTS);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = useCallback((next) => {
    setSettings((prev) => {
      const merged = { ...prev, ...next };
      applyToDocument(merged);
      return merged;
    });
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, loaded, refresh: load, update }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used inside SettingsProvider");
  return ctx;
}
